import { makeVar, useReactiveVar } from '@apollo/client'
import { MessageDescriptor } from '@lingui/core'

import getHrefLang from './getHrefLang'

export interface StoreView {
  /**
   * The string identifier of the store as defined in M2.
   */
  code: string
  /**
   * A regex or function to match against the current host. Can be `undefined`
   * if the location is not relevant (e.g. for the default).
   * When multiple StoreViews match the location, the first StoreView in the
   * storeViews array will be used.
   */
  location: RegExp | ((location: Location) => boolean) | undefined
  /**
   * Used to generate a relative path for redirects.
   * This could probably be refactored to output an absolute URL using
   * window.location.origin, should that be desirable.
   */
  makeUrl: (relativePath: string) => string
  /**
   * The app's basename. Used by react-router to automatically account for it
   * in routes or add it to links.
   * See: https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/api/BrowserRouter.md#basename-string
   */
  basename: string
  /**
   * Group, this can be used to group storeViews. Used by StoreViewSelector
   */
  group?: string
  // ISO 639-1 language code
  language: string
  // example: 'nl-nl' or 'nl-be', undefined when using non-locale language
  locale: string | undefined
  name: MessageDescriptor
  /**
   * Whether this is the default StoreView. Used when no StoreView has a
   * matching `location`.
   */
  default: boolean
  storeId: number
  websiteId: number
  /**
   * Optional default display currency code, will be overwritten by storeConfig
   * query when available. This might change in the future.
   */
  defaultDisplayCurrencyCode?: string
  /**
   * Optional tag name for prismic.io to support multi-domain sites
   */
  prismic?: string
  prismicLocale?: string
}

export interface StoreViewWithState extends StoreView {
  /** Whether this is the currently active StoreView. */
  active: boolean
  hrefLang: string
}

const storeViewsWithState = makeVar<StoreViewWithState[]>([])

export function getStoreViews() {
  const storeViews = storeViewsWithState()

  if (!storeViews?.length) {
    throw new Error('StoreViews not setup correctly.')
  }

  return storeViewsWithState()
}

export function useStoreViews() {
  const storeViews = useReactiveVar(storeViewsWithState)

  if (!storeViews?.length) {
    throw new Error('StoreViews not setup correctly.')
  }

  return storeViews
}

export function setStoreViews(
  storeViews: StoreView[],
  activeStoreView: StoreView,
) {
  storeViewsWithState(
    storeViews.map<StoreViewWithState>((storeView) => ({
      ...storeView,
      active: storeView === activeStoreView,
      hrefLang: getHrefLang(storeView),
    })),
  )
}

type StoreViewChangeListener = (storeViews: StoreViewWithState[]) => void
export const storeViewChangeListeners = new Set<StoreViewChangeListener>()

function onStoreViewChange(newStoreViews: StoreViewWithState[]) {
  storeViewChangeListeners.forEach((callback) => callback(newStoreViews))
  storeViewsWithState.onNextChange(onStoreViewChange)
}

storeViewsWithState.onNextChange(onStoreViewChange)
